import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronUp, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SimpleInputs } from "./SimpleInputs";
import { OverheadTable } from "./OverheadTable";
import { ResultsPanel } from "./ResultsPanel";
import { CostFrequencies } from "./Section";
import { StaffRoster } from "./StaffRoster";
import { SensitivityTable } from "./SensitivityTable";
import { HiddenCostPanel, AssumptionsPanel } from "./HiddenCostPanel";
import { TrendTracking } from "./TrendTracking";
import { computeRate, money, clamp, n } from "./rateMath";

const LIMITS = { hours_per_week: 168, utilization_pct: 100, downtime_weeks: 52, margin_pct: 99 };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fmtStamp = (iso) =>
  new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });

const AdvancedView = ({ inputs, set, result, snapshots, overheadBaseline, currentRate }) => (
  <div className="space-y-4 mt-4" data-testid="rate-calc-advanced">
    <CostFrequencies result={result} />
    <SensitivityTable inputs={inputs} result={result} />
    <HiddenCostPanel inputs={inputs} result={result} overheadBaseline={overheadBaseline} currentRate={currentRate} />
    <AssumptionsPanel />
    <TrendTracking snapshots={snapshots} />
  </div>
);

export default function RateCalculatorTab({ currentLabourRate, onApply }) {
  const [inputs, setInputs] = useState(null);
  const [saved, setSaved] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const loadSnapshots = async () => {
    const res = await axios.get(`${API}/settings/rate-calculator/snapshots`, { headers: authHeaders() });
    setSnapshots(res.data || []);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/rate-calculator`, { headers: authHeaders() });
        setInputs(res.data.inputs);
        setSaved(res.data.inputs);
        setUpdatedAt(res.data.updated_at);
        await loadSnapshots();
      } catch (e) {
        toast.error("Failed to load Rate Calculator");
      }
    })();
  }, []);

  const result = useMemo(() => (inputs ? computeRate(inputs) : null), [inputs]);
  const isDirty = inputs && saved && JSON.stringify(inputs) !== JSON.stringify(saved);

  const overheadBaseline = useMemo(() => {
    const snap = isDirty ? snapshots[snapshots.length - 1] : snapshots[snapshots.length - 2];
    return snap ? { monthly: snap.total_overhead_yearly / 12, date: snap.saved_at } : null;
  }, [snapshots, isDirty]);

  const set = (patch) =>
    setInputs((prev) => {
      const next = { ...prev, ...patch };
      Object.entries(LIMITS).forEach(([k, max]) => (next[k] = clamp(n(next[k]), 0, max)));
      return next;
    });

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const res = await axios.put(`${API}/settings/rate-calculator`, inputs, { headers: authHeaders() });
      setInputs(res.data.inputs);
      setSaved(res.data.inputs);
      setUpdatedAt(res.data.updated_at);
      await loadSnapshots();
      toast.success("Rate Calculator saved");
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Failed to save Rate Calculator");
    } finally {
      setIsSaving(false);
    }
  };

  if (!inputs) return <div className="text-center py-12 text-gray-400 animate-pulse">Loading calculator...</div>;

  const currentRate = Number.isFinite(+currentLabourRate) ? +currentLabourRate : null;
  const belowBy = currentRate !== null && result.trueHourly > currentRate ? result.trueHourly - currentRate : 0;

  return (
    <div className="max-w-6xl" data-testid="rate-calculator-tab">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Shop Labour Rate Calculator</h3>
          <p className="text-xs text-gray-500 mt-1 max-w-xl">
            Works out what an hour of your shop really costs — wages plus overhead — then adds your margin to suggest a Labour Rate.
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="text-[11px] text-gray-500" data-testid="rate-calc-last-updated">
              Last updated: {updatedAt ? fmtStamp(updatedAt) : "never saved"}
            </span>
            {isDirty && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300" data-testid="rate-calc-unsaved">Unsaved changes</Badge>}
            {belowBy > 0.005 && (
              <Badge className="text-[10px] bg-red-600 hover:bg-red-600" data-testid="rate-calc-drift-badge">
                <AlertTriangle className="w-3 h-3 mr-1" /> Your current rate is {money(belowBy)} below break-even
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button size="sm" onClick={handleSave} disabled={isSaving || !isDirty} className="h-9 text-xs" data-testid="rate-calc-save-btn">
            <Save className="w-3 h-3 mr-1.5" />
            {isSaving ? "Saving..." : isDirty ? "Save*" : "Save"}
          </Button>
          <span className="text-[11px] text-gray-500" data-testid="rate-calc-save-note">Saving records a snapshot to your rate history.</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-5 items-start">
        <div className="space-y-4 min-w-0">
          <StaffRoster rows={inputs.staff} onRowsChange={(rows) => set({ staff: rows })} weeklyTotal={result.weeklyLabour} />
          <SimpleInputs inputs={inputs} set={set} />
          <OverheadTable rows={inputs.overheads} onRowsChange={(rows) => set({ overheads: rows })} yearlyTotal={result.annualOverhead} />
        </div>
        <ResultsPanel result={result} marginPct={inputs.margin_pct} onApply={onApply} />
      </div>

      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-pink-600 hover:text-pink-700"
        data-testid="rate-calc-advanced-toggle"
      >
        {showAdvanced ? "Hide advanced options" : "Show advanced options"}
        {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {showAdvanced && (
        <AdvancedView inputs={inputs} set={set} result={result} snapshots={snapshots} overheadBaseline={overheadBaseline} currentRate={currentRate} />
      )}
    </div>
  );
}
